import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { columnFiltersToQueryParams, pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useSubMerchants } from "@/features/merchant/api/use-sub-merchants";
import { MerchantFormDialog } from "@/features/merchant/components/merchants/merchant-form-dialog";
import {
  merchantsColumns,
  type MerchantRow,
} from "@/features/merchant/components/merchants/merchants-columns";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

/** Section « Sous-marchands » : liste + création (avec idempotence). */
export function SubMerchantsSection({ masterId }: { masterId: string }) {
  const { t: tm } = useMerchantTranslation();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: columnFiltersToQueryParams(columnFilters),
    }),
    [pagination, columnFilters]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useSubMerchants(masterId, params);

  const rows = useMemo(
    () => (data?.items ?? []).filter((m): m is MerchantRow => Boolean(m.id)),
    [data]
  );

  const columns = useMemo(() => merchantsColumns(navigate), [navigate]);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          {tm("merchant.merchants.subMerchants.title")}
        </h2>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tm("merchant.merchants.subMerchants.create")}
        </Button>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={() => columns}
        title={tm("merchant.merchants.subMerchants.title")}
        exportFileName="sub-merchants"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <MerchantFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        masterId={masterId}
      />
    </section>
  );
}
